import {
  MemberApplicationPaymentRequestCapability,
  MemberApplicationPaymentRequestInvoiceEvidence,
  MemberApplicationPaymentRequestResolution,
  resolveMemberApplicationPaymentRequestCapabilities,
} from "./memberApplicationPaymentRequestCapabilities";
import { sendOnlyUnsentCreatedMemberInvoices } from "./memberInvoiceDelivery";
import {
  MemberLoginAvailabilityEvidence,
  memberLoginEmailInUseCode,
} from "./memberLoginAvailabilityFlow";

export type CreatedMemberApplicationMutationPayload<
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
> = {
  applicationSubmittedSuccessfully: boolean;
  blockedByDuplicate: boolean;
  loginAvailability?: MemberLoginAvailabilityEvidence | null;
  invoice?: Invoice | null;
  addOnInvoice?: Invoice | null;
  paymentRequestCapabilities?: ReadonlyArray<MemberApplicationPaymentRequestCapability> | null;
};

export const runCreatedMemberApplicationMutationFlow = async <
  Input,
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
  Payload extends CreatedMemberApplicationMutationPayload<Invoice>,
>({
  input,
  submit,
}: {
  input: Input;
  submit: (input: Input) => Promise<Payload | null | undefined>;
}) => {
  const mutationResult = await submit(input);

  if (!mutationResult) {
    throw new Error("No submit application response was returned.");
  }

  if (
    !mutationResult.applicationSubmittedSuccessfully &&
    !mutationResult.blockedByDuplicate &&
    mutationResult.loginAvailability?.code !== memberLoginEmailInUseCode
  ) {
    throw new Error(
      "Chamber Pro did not confirm the membership application. Review the member list before retrying."
    );
  }

  const invoiceTargets = [
    ...(mutationResult.invoice
      ? [{ kind: "membership" as const, invoice: mutationResult.invoice }]
      : []),
    ...(mutationResult.addOnInvoice
      ? [{ kind: "add-on" as const, invoice: mutationResult.addOnInvoice }]
      : []),
  ];
  const paymentRequestResolution =
    resolveMemberApplicationPaymentRequestCapabilities({
      capabilities: mutationResult.paymentRequestCapabilities || [],
      invoices: invoiceTargets,
    });

  return { mutationResult, paymentRequestResolution };
};

export const runCreatedMemberPaymentRequestDeliveryFlow = async <
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
  DeliveryResult,
>({
  getFailure,
  paymentRequestResolution,
  send,
  wasDeliveryConfirmed,
}: {
  getFailure: (
    target: MemberApplicationPaymentRequestResolution<Invoice>["sendable"][number],
    deliveryResult: DeliveryResult
  ) => Error;
  paymentRequestResolution: MemberApplicationPaymentRequestResolution<Invoice>;
  send: (
    target: MemberApplicationPaymentRequestResolution<Invoice>["sendable"][number]
  ) => Promise<DeliveryResult>;
  wasDeliveryConfirmed: (deliveryResult: DeliveryResult) => boolean;
}) =>
  sendOnlyUnsentCreatedMemberInvoices({
    getFailure,
    invoices: paymentRequestResolution.sendable,
    send,
    wasDeliveryConfirmed,
  });
